import { EventStreamContentType, fetchEventSource } from "@microsoft/fetch-event-source";
import { GetIDToken } from "../../firebase/FirebaseConfig";
import { ProjectAnnotation, ProjectMessage } from "./types";

export interface ProjectStreamMessageCreated {
  responseID?: string;
  messageID: string;
  conversationID?: string;
}

export interface ProjectStreamCallbacks {
  onMessageCreated?: (event: ProjectStreamMessageCreated) => void;
  onMessageDelta: (delta: string) => void;
  onMessageCompleted: (message: ProjectMessage) => void;
}

export const projectMessageFromData = (data: any): ProjectMessage => {
  const sentAt = parseOptionalDate(data?.sentAt);
  const annotations: ProjectAnnotation[] = Array.isArray(data?.annotations) ? data.annotations.map((annotation: ProjectAnnotation) => ({ ...annotation })) : [];

  return {
    id: data.id,
    ...(sentAt ? { sentAt } : {}),
    content: data.content,
    sender: data.sender,
    annotations,
  };
};

const parseOptionalDate = (value: unknown): Date | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value as string | number);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

export async function StreamProjectResponse(url: string, body: object, callbacks: ProjectStreamCallbacks, signal: AbortSignal): Promise<void> {
  const idToken = await GetIDToken();
  let completed = false;

  await fetchEventSource(url, {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    signal,
    openWhenHidden: true,
    async onopen(response) {
      if (response.ok && response.headers.get("content-type")?.startsWith(EventStreamContentType)) return;

      let message = "Failed to open stream.";
      try {
        const payload = await response.json();
        if (typeof payload?.message === "string") message = payload.message;
      } catch {
        // Keep the generic error when the response is not a server envelope.
      }
      throw new Error(message);
    },
    onmessage(event) {
      if (event.event === "message_created") {
        callbacks.onMessageCreated?.(JSON.parse(event.data) as ProjectStreamMessageCreated);
      } else if (event.event === "message_delta") {
        const { delta } = JSON.parse(event.data) as { delta?: unknown };
        if (typeof delta === "string") callbacks.onMessageDelta(delta);
      } else if (event.event === "message_completed") {
        completed = true;
        callbacks.onMessageCompleted(projectMessageFromData(JSON.parse(event.data)));
      } else if (event.event === "stream_error") {
        const payload = JSON.parse(event.data) as { message?: unknown };
        throw new Error(typeof payload.message === "string" ? payload.message : "Stream failed.");
      }
    },
    onclose() {
      if (!completed && !signal.aborted) throw new Error("Stream closed before the response completed.");
    },
    onerror(error) {
      throw error;
    },
  });
}
